var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import React from "react";
import styled, { css } from "styled-components";
import EmptyState from "@atlaskit/empty-state";
import { Link } from "@atlassiansox/microscopekit";
import Button, { LoadingButton } from "@atlaskit/button";
import Lozenge from "@atlaskit/lozenge";
import { colors } from "@atlaskit/theme";
import illustration from "./illustration.svg";
import { wargameExerciseStatuses } from "../../../models";
import { useMutationUI } from "../../../hooks";
import { UpdateWargameExerciseStatusMutation } from "../../../graphql";
export var EmptyExercise = function (_a) {
    var onImportPlan = _a.onImportPlan, exerciseId = _a.exerciseId, exerciseStatus = _a.exerciseStatus, confluencePage = _a.confluencePage, jiraUrl = _a.jiraUrl;
    if (exerciseStatus === wargameExerciseStatuses.cancelled.name) {
        return (React.createElement(EmptyState, { header: "This wargame exercise has been cancelled", imageUrl: illustration, description: React.createElement(React.Fragment, null,
                "Go to the ",
                React.createElement(Link, { to: "../" }, "home page"),
                " to create a new exercise") }));
    }
    var ticketId = jiraUrl.split("/").reverse()[0];
    return (React.createElement(EmptyState, { header: "You're on your way!", imageUrl: illustration, description: React.createElement(Steps, null,
            React.createElement(Step, { isCompleted: isStepCompleted(exerciseStatus, wargameExerciseStatuses.todo.name) },
                React.createElement("strong", null, "To do: "),
                "Wargame is an exercise that increases resilience by practicing failure scenarios and discovering mitigations without customer impact. Please read more details at",
                " ",
                React.createElement("a", { href: "http://go/wargameplaybook", target: "_blank", rel: "noopener noreferrer" }, "go/wargameplaybook"),
                " ",
                React.createElement(CompletedStep, { exerciseStatus: exerciseStatus, stepStatus: wargameExerciseStatuses.todo.name })),
            React.createElement(Step, { isCompleted: isStepCompleted(exerciseStatus, wargameExerciseStatuses.brainstormAndWalkthrough.name) },
                React.createElement("strong", null, "Brainstorm and Walkthrough: "),
                "Fill details in the",
                " ",
                React.createElement("a", { href: confluencePage, target: "_blank", rel: "noopener noreferrer" }, "Experiment Form"),
                ". Start with System Design and cover experiments either from the recent incidents or any critical dependencies of upstream or downstream services which could disrupt your service. Visit",
                " ",
                React.createElement("a", { href: "http://go/fmea", target: "_blank", rel: "noopener noreferrer" }, "go/fmea"),
                " ",
                "for more details.",
                " ",
                React.createElement(CompletedStep, { exerciseStatus: exerciseStatus, stepStatus: wargameExerciseStatuses.brainstormAndWalkthrough.name })),
            React.createElement(Step, { isCompleted: isStepCompleted(exerciseStatus, wargameExerciseStatuses.prepareExperiments.name) },
                React.createElement("strong", null, "Prepare Experiments: "),
                " Choose one or more fault injections experiment and the target to run them on. Decide how long to run each experiment and when to inject faults in the experiment. Brahmos is integrated with Perfhammer which you can use to generate load. Need help? Visit",
                " ",
                React.createElement("a", { href: "http://go/brahmos", target: "_blank", rel: "noopener noreferrer" }, "go/brahmos"),
                " ",
                "or",
                " ",
                React.createElement("a", { href: "http://go/wargameplaybook", target: "_blank", rel: "noopener noreferrer" }, "go/wargameplaybook"),
                " ",
                React.createElement(CompletedStep, { exerciseStatus: exerciseStatus, stepStatus: wargameExerciseStatuses.prepareExperiments.name })),
            React.createElement("li", null,
                React.createElement("strong", null, "Execute Experiments: "),
                "Start running your experiments for",
                " ",
                React.createElement("a", { href: jiraUrl, target: "_blank", rel: "noopener noreferrer" }, ticketId),
                ". As you discover mitigations or weaknesses in your service you'll log them as WarGame Priority Action items and link them Experiment tickets."),
            React.createElement("li", null,
                React.createElement("strong", null, "Done: "),
                "Congratulations, your exercise is completed! Please capture the priority actions item in the WG ticket")), primaryAction: React.createElement(PrimaryAction, { exerciseId: exerciseId, exerciseStatus: exerciseStatus, onImportPlan: onImportPlan }), secondaryAction: React.createElement(SecondaryAction, { exerciseStatus: exerciseStatus, exerciseId: exerciseId }) }));
};
var PrimaryAction = function (_a) {
    var _b;
    var exerciseId = _a.exerciseId, exerciseStatus = _a.exerciseStatus, onImportPlan = _a.onImportPlan;
    var _c = useMutationUI({
        mutation: UpdateWargameExerciseStatusMutation,
        successMessage: "Exercise status updated successfully"
    }), mutate = _c.mutate, isLoading = _c.isLoading;
    var onClick = function () {
        var _a;
        if (exerciseStatus === wargameExerciseStatuses.prepareExperiments.name) {
            onImportPlan();
        }
        else {
            var nextStatusMapping = (_a = {},
                _a[wargameExerciseStatuses.todo.name] = wargameExerciseStatuses.brainstormAndWalkthrough.name,
                _a[wargameExerciseStatuses.brainstormAndWalkthrough.name] = wargameExerciseStatuses.prepareExperiments.name,
                _a);
            var nextStatus = nextStatusMapping[exerciseStatus];
            mutate({ variables: { exerciseID: exerciseId, status: nextStatus } });
        }
    };
    var buttonTextMapping = (_b = {},
        _b[wargameExerciseStatuses.todo.name] = "Start Brainstorming!",
        _b[wargameExerciseStatuses.brainstormAndWalkthrough.name] = "Prepare Experiments",
        _b[wargameExerciseStatuses.prepareExperiments.name] = "Import Wargame Plan",
        _b);
    var buttonText = buttonTextMapping[exerciseStatus];
    return (React.createElement(LoadingButton, { appearance: "primary", isLoading: isLoading, onClick: onClick, testId: "wargameExercisePrimaryAction" }, buttonText));
};
var SecondaryAction = function (_a) {
    var exerciseStatus = _a.exerciseStatus, exerciseId = _a.exerciseId;
    if (exerciseStatus !== wargameExerciseStatuses.prepareExperiments.name) {
        return null;
    }
    return (React.createElement(Link, { to: "../create-template?wargameExerciseId=" + exerciseId },
        React.createElement(Button, { testId: "wargameExerciseSecondaryAction" }, "Create New Wargame Plan")));
};
var Steps = styled.ol(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  text-align: left;\n"], ["\n  text-align: left;\n"])));
var Step = styled.li(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", "\n"], ["\n  ",
    "\n"])), function (_a) {
    var isCompleted = _a.isCompleted;
    return isCompleted && css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n      color: ", ";\n    "], ["\n      color: ", ";\n    "])), colors.N50);
});
var CompletedStep = function (_a) {
    var exerciseStatus = _a.exerciseStatus, stepStatus = _a.stepStatus;
    return isStepCompleted(exerciseStatus, stepStatus) ? (React.createElement(Lozenge, { appearance: "success" }, "Done")) : null;
};
var isStepCompleted = function (exerciseStatus, stepStatus) {
    var exerciseStatusIndex = Object.values(wargameExerciseStatuses).findIndex(function (status) { return status.name === exerciseStatus; });
    var stepStatusIndex = Object.values(wargameExerciseStatuses).findIndex(function (status) { return status.name === stepStatus; });
    return (exerciseStatus !== wargameExerciseStatuses.cancelled.name &&
        stepStatusIndex < exerciseStatusIndex);
};
var templateObject_1, templateObject_2, templateObject_3;
